import { Injectable } from '@angular/core';
import { environment } from '@environment';
import {
  AccessCodeShown,
  Ampli,
  ContractCreateCancelled,
  ContractCreateLicensePlateAdded,
  ContractCreateLicensePlateRemoved,
  ContractCreateOpened,
  ContractCreateSaved,
  ContractEditCancelled,
  ContractEditLicensePlateAdded,
  ContractEditLicensePlateRemoved,
  ContractEditOpened,
  ContractEditSaved,
  ContractEditTerminateCancelled,
  ContractEditTerminateClicked,
  ContractEditTerminateSubmitted,
  IdentifyProperties,
  InvoiceDownloaded,
  LanguageChanged,
  LoggedIn,
  LoggedOut,
  PaymentMethodAddClicked,
  PaymentMethodAddFailed,
  PaymentMethodAddSucceeded,
  PayPerUseLicensePlateAdded,
  PayPerUseLicensePlateRemoved,
  RegistrationEmailSubmitted,
  RegistrationEmailValidated,
  RegistrationLicensePlateSubmitted,
  RegistrationParkingSessionStatusFetched,
  RegistrationResendEmailClicked,
  RegistrationStarted,
} from '@integrations/amplitude';
import * as Sentry from '@sentry/angular';
import { FeatureFlagService } from './feature-flag.service';

type AllowedAmplitudeEvents =
  | AccessCodeShown
  | ContractCreateCancelled
  | ContractCreateLicensePlateAdded
  | ContractCreateLicensePlateRemoved
  | ContractCreateOpened
  | ContractCreateSaved
  | ContractEditCancelled
  | ContractEditLicensePlateAdded
  | ContractEditLicensePlateRemoved
  | ContractEditOpened
  | ContractEditSaved
  | ContractEditTerminateCancelled
  | ContractEditTerminateClicked
  | ContractEditTerminateSubmitted
  | InvoiceDownloaded
  | LanguageChanged
  | LoggedIn
  | LoggedOut
  | PaymentMethodAddClicked
  | PaymentMethodAddFailed
  | PaymentMethodAddSucceeded
  | PayPerUseLicensePlateAdded
  | PayPerUseLicensePlateRemoved
  | RegistrationEmailSubmitted
  | RegistrationEmailValidated
  | RegistrationLicensePlateSubmitted
  | RegistrationParkingSessionStatusFetched
  | RegistrationResendEmailClicked
  | RegistrationStarted;

@Injectable({
  providedIn: 'root',
})
export class AmplitudeEventService {
  private amplitudeInitialized = false;
  private ampli: Ampli | null = null;
  private userId: string | null = null;
  //We use this to cache identify properties until Amplitude is initialized and we have a userId
  private pendingIdentifyProperties: Partial<IdentifyProperties> = {};

  constructor(private featureFlagService: FeatureFlagService) {}

  updateUserProperties(payload: { userId?: string; properties: Partial<IdentifyProperties> }) {
    if (payload.userId) {
      this.userId = payload.userId;
    }

    this.pendingIdentifyProperties = { ...(this.pendingIdentifyProperties ?? {}), ...payload.properties };
    if (this.amplitudeInitialized && this.ampli && this.userId) {
      this.ampli.identify(this.userId, this.pendingIdentifyProperties);
      this.pendingIdentifyProperties = {};
    }
  }

  trackEvent(event: AllowedAmplitudeEvents) {
    this.debugLogAmplitudeEvents(event);

    if (!this.amplitudeInitialized || !this.ampli) {
      return;
    }

    try {
      this.ampli.track(event);
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  initializeAmplitude = async () => {
    if (this.amplitudeInitialized || !environment.amplitude_key || !this.featureFlagService.getFeatureFlag('enable_amplitude')) {
      return;
    }

    // We import this here so it is only loaded when we need it due to feature flag and environment settings
    this.ampli = await import('@integrations/amplitude')
      .then((m) => m.ampli)
      .catch((error) => {
        Sentry.captureException(error);
        return null;
      });

    if (!this.ampli) {
      return;
    }

    this.ampli.load({
      client: {
        apiKey: environment.amplitude_key,
        configuration: {
          autocapture: {
            pageViews: {
              trackOn: () => {
                // Do not track base64 URLS (login and registration magic links)
                // Our JWTS right now all start with eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9 --decoded-> {"alg":"HS256","typ":"JWT"}
                return !window.location.pathname.toLowerCase().includes('/ey');
              },
            },
          },
          serverZone: 'EU',
        },
      },
    });

    // Experiment.initializeWithAmplitudeAnalytics(environment.amplitude_key, {
    //   serverZone: 'EU',
    // });

    // Load visual editor only in the browser, only once, and not in production environment. This might change in the future, but then we still need to gatekeep it so not everyone has to load it.
    if (typeof document !== 'undefined' && typeof window !== 'undefined' && environment.name !== 'production') {
      if (!document.getElementById('amplitude-visual-web-editor')) {
        try {
          const visualWebEditor = document.createElement('script');
          visualWebEditor.id = 'amplitude-visual-web-editor';
          visualWebEditor.async = true;
          visualWebEditor.defer = true;
          visualWebEditor.src = 'https://cdn.eu.amplitude.com/script/' + environment.amplitude_key + '.experiment.js';
          document.head?.appendChild(visualWebEditor);
        } catch (error) {
          Sentry.captureException(error);
        }
      }
    }
    this.amplitudeInitialized = true;
  };

  debugLogAmplitudeEvents(event: AllowedAmplitudeEvents) {
    if (environment.name !== 'production') {
      const msg = environment.name === 'development' ? 'Amplitude would track:' : 'Amplitude is tracking:';
      console.info(msg, event.event_type);
    }
  }

  buildContractParkingSpacesPayload(max: number, used: number) {
    return {
      contractParkingSpacesAvailable: max,
      contractParkingSpacesUsed: used,
      contractParkingSpacesLimitMet: used === max,
      contractParkingSpacesLimitOver: used > max,
      contractParkingSpacesLimitUnder: used < max,
    };
  }
}
